import { AppThunk } from "../store";
import { authService } from "services/authService";
import {
  setCredentials,
  setSavedCredentials,
  updateIsAuthenticated,
  getSavedCredentials,
  clearSavedCredentials,
  logOut,
  confirmSession,
  updateSessionStatus,
  setEmbedToken,
  setSavedUsername,
  getUserEmail,
  clearSavedUsername,
  setOtpAuthUrl,
  setTwoFAUpdateStatus,
  setTwoFactorAuthEnabled,
  setTwoFactorValidated,
  setLoginRequestStatus,
} from ".";
import { SessionStatus } from "types/Session";
import { RequestStatus as Status } from "constants/RequestStatus";

export interface EmailAuth {
  email: string;
  password: string;
  rememberMe: boolean;
}
export const logInWithExchange =
  (token: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoginRequestStatus(Status.Loading));
      const { data } = await authService.exchange(token);
      setSavedCredentials(data);
    } catch (error) {
      console.error(error);
      dispatch(setLoginRequestStatus(Status.Failed));
    }
    dispatch(initSession());
  };
export const logInWithEmail =
  (authInfo: EmailAuth): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setLoginRequestStatus(Status.Loading));
      const { email, password, rememberMe } = authInfo;
      const { data } = await authService.login(email, password);
      setSavedCredentials(data);
      if (rememberMe) {
        setSavedUsername(email);
      } else if (getUserEmail()) {
        clearSavedUsername();
      }
    } catch (error) {
      clearSavedUsername();
      dispatch(setLoginRequestStatus(Status.Failed));
    }
    dispatch(initSession());
  };
export const initSession = (): AppThunk => async (dispatch) => {
  const credentials = getSavedCredentials();

  if (!credentials) {
    dispatch(confirmSession());
    dispatch(updateSessionStatus(SessionStatus.logOut));
    return;
  }
  if (credentials.userOtpVerified && !credentials.userOtpValidated) {
    dispatch(setTwoFactorAuthEnabled(true));
    dispatch(updateSessionStatus(SessionStatus.waitingTwoFactorAuth));
    dispatch(setCredentials(credentials));
  } else {
    try {
      dispatch(updateCredentials(credentials));
      dispatch(updateSessionStatus(SessionStatus.fulfilled));
      dispatch(setLoginRequestStatus(Status.Success));
    } catch (error) {
      clearSavedCredentials();
      dispatch(setLoginRequestStatus(Status.Failed));
      dispatch(logOut());
    } finally {
      dispatch(confirmSession());
    }
  }
};
export const twoFactorAuthSession =
  (validateToken: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setTwoFactorValidated(Status.Loading));
      const {
        data: { otpVerified, credentials },
      } = await authService.verifyOtp(validateToken);
      const newCredentials = {
        ...credentials,
        userOtpValidated: true,
      };
      dispatch(updateCredentials(newCredentials));
      dispatch(updateSessionStatus(SessionStatus.fulfilled));
      dispatch(setTwoFactorValidated(Status.Success));
      dispatch(setLoginRequestStatus(Status.Success));
    } catch (error) {
      dispatch(setTwoFactorValidated(Status.Failed));
      dispatch(setLoginRequestStatus(Status.Failed));
    } finally {
      dispatch(confirmSession());
    }
  };
export const closeSession = (): AppThunk => async (dispatch) => {
  try {
    await authService.logout();
  } finally {
    clearSavedCredentials();
    dispatch(logOut());
  }
};

export const updateCredentials =
  (credentials: any): AppThunk =>
  async (dispatch) => {
    setSavedCredentials(credentials);
    dispatch(updateIsAuthenticated(true));
    dispatch(setCredentials(credentials));
  };

export const getEmbedToken =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      const { data } = await authService.embedToken(id);
      dispatch(setEmbedToken(data));
    } catch (error) {
      console.error("Error fetching access token:", error);
    }
  };

export const getOtpAuthUrl = (): AppThunk => async (dispatch) => {
  try {
    const { data } = await authService.generateOtp();
    dispatch(setOtpAuthUrl(data.otpAuthUrl));
  } catch (error) {
    console.error("Error fetching access token:", error);
  }
};

export const verifyOtpAndActivate2FA =
  (otpToken: any): AppThunk =>
  async (dispatch) => {
    try {
      const {
        data: { otpVerified, credentials },
      } = await authService.verifyOtp(otpToken);
      setSavedCredentials(credentials);
      dispatch(setCredentials(credentials));
      dispatch(setTwoFactorAuthEnabled(otpVerified));
      // If our activation req is successful, we'll receive a response with the otp verified set in true
      if (otpVerified === true) {
        dispatch(setTwoFAUpdateStatus(Status.Success));
      } else {
        throw new Error();
      }
    } catch (error) {
      dispatch(setTwoFAUpdateStatus(Status.Failed));
    }
  };

export const disable2FA = (): AppThunk => async (dispatch) => {
  try {
    const {
      data: { otpEnabled, credentials },
    } = await authService.disableOtp();
    dispatch(updateCredentials(credentials));
    // If our disable req is successful, we'll receive a response with the otp enabled set in false
    if (otpEnabled === false) {
      dispatch(setOtpAuthUrl(null));
      dispatch(setTwoFAUpdateStatus(Status.Success));
      dispatch(setTwoFactorAuthEnabled(false));
    } else {
      throw new Error();
    }
  } catch (error) {
    dispatch(setTwoFAUpdateStatus(Status.Failed));
  }
};
